import { router } from "@inertiajs/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { MagicCard } from "../../../components/magicui/magic-card";
import { ShimmerButton } from "../../../components/magicui/shimmer-button";
import { SparklesText } from "../../../components/magicui/sparkles-text";
import { cn } from "../../../lib/utils";

const providerMeta = {
  fal: {
    label: "fal.ai",
    eyebrow: "Image + video",
    cardTone: "from-fuchsia-500/18 via-violet-500/12 to-slate-950",
    logoTone: "border-fuchsia-400/35 bg-fuchsia-500/12 text-fuchsia-100 shadow-[0_0_30px_rgba(217,70,239,0.18)]"
  },
  wavespeed: {
    label: "Wavespeed",
    eyebrow: "Image generation",
    cardTone: "from-sky-500/18 via-cyan-500/12 to-slate-950",
    logoTone: "border-sky-400/35 bg-sky-500/12 text-sky-100 shadow-[0_0_30px_rgba(14,165,233,0.18)]"
  },
  openrouter: {
    label: "OpenRouter",
    eyebrow: "Copy orchestration",
    cardTone: "from-emerald-500/18 via-teal-500/12 to-slate-950",
    logoTone: "border-emerald-400/35 bg-emerald-500/12 text-emerald-100 shadow-[0_0_30px_rgba(16,185,129,0.18)]"
  },
  elevenlabs: {
    label: "ElevenLabs",
    eyebrow: "Voice + TTS",
    cardTone: "from-amber-500/18 via-orange-500/12 to-slate-950",
    logoTone: "border-amber-300/35 bg-amber-500/12 text-amber-50 shadow-[0_0_30px_rgba(251,191,36,0.18)]"
  }
};

const META_OAUTH_SCOPES = [
  "instagram_basic",
  "instagram_content_publish",
  "pages_show_list",
  "pages_read_engagement"
];

const TIKTOK_OAUTH_SCOPES = [
  "user.info.basic",
  "video.publish"
];

const DISCOUNT_CODE_TYPES = [
  { value: "ambassador_free_access", label: "Ambassador free access" },
  { value: "percentage_off", label: "Percentage off" },
  { value: "fixed_amount_off", label: "Fixed amount off" },
  { value: "free_months", label: "Free months" }
];

const DISCOUNT_DURATION_UNITS = [
  { value: "months", label: "Months" },
  { value: "years", label: "Years" }
];

function buildDiscountCodeDraft() {
  return {
    code: "",
    kind: "ambassador_free_access",
    plan_id: "",
    duration_value: "1",
    duration_unit: "months",
    percent_off: "20",
    amount_off: "10",
    free_months: "1",
    recipient_email: "",
    active: true
  };
}

function buildSecretEditorState() {
  return {
    api_keys: {},
    meta_app_secret: false,
    tiktok_client_secret: false
  };
}

function buildSecretDraftState() {
  return {
    api_keys: {},
    meta_app_secret: "",
    tiktok_client_secret: ""
  };
}

const providerLabels = {
  fal: providerMeta.fal.label,
  wavespeed: providerMeta.wavespeed.label,
  openrouter: providerMeta.openrouter.label,
  elevenlabs: providerMeta.elevenlabs.label
};

const imageProviders = [
  { value: "fal", label: "fal.ai" },
  { value: "wavespeed", label: "Wavespeed" }
];

const FAL_IMAGE_RESOLUTION_OPTIONS_BY_MODEL = {
  "fal-ai/nano-banana-pro": ["1K", "2K", "4K"],
  "fal-ai/nano-banana-2": ["0.5K", "1K", "2K", "4K"],
  "fal-ai/nano-banana-pro/edit": ["1K", "2K", "4K"],
  "fal-ai/nano-banana-2/edit": ["0.5K", "1K", "2K", "4K"]
};

const WAVESPEED_IMAGE_RESOLUTION_OPTIONS_BY_MODEL = {
  "google/nano-banana-pro/text-to-image": ["1K", "2K", "4K"],
  "google/nano-banana-2/text-to-image": ["1K", "2K", "4K"],
  "google/nano-banana-pro/edit": ["1K", "2K", "4K"],
  "google/nano-banana-2/edit": ["1K", "2K", "4K"]
};

const VOICE_ACCESS_CATEGORY_ORDER = ["professional", "cloned", "generated", "premade", "default", "community", "saved", "workspace", "uncategorized"];
const VOICE_ACCESS_CATEGORY_LABELS = {
  professional: "Professional",
  cloned: "Cloned",
  generated: "Generated",
  premade: "Premade",
  default: "Default",
  community: "Community",
  saved: "Saved",
  workspace: "Workspace",
  uncategorized: "Other"
};

function BrandLogo({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="admin-brand-logo-gradient" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset="0.5" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#020617" stroke="url(#admin-brand-logo-gradient)" strokeWidth="3" />
      <path
        d="M17 45L30.5 17.5a2 2 0 013 0L47 45a2 2 0 01-1.8 2.9H18.8A2 2 0 0117 45z"
        fill="none"
        stroke="url(#admin-brand-logo-gradient)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="33" r="5.5" fill="url(#admin-brand-logo-gradient)" />
    </svg>
  );
}

export default function AdminSettingsIndex({ platform_setting: initialPlatformSetting, plans: initialPlans = [], discount_codes: initialDiscountCodes = [], auth }) {
  const [platformSetting, setPlatformSetting] = useState(initialPlatformSetting);
  const [plans, setPlans] = useState(initialPlans);
  const [discountCodes, setDiscountCodes] = useState(initialDiscountCodes);
  const [discountCodeDraft, setDiscountCodeDraft] = useState(() => buildDiscountCodeDraft());
  const [discountEmailDrafts, setDiscountEmailDrafts] = useState({});
  const [providerCatalog, setProviderCatalog] = useState({});
  const [modelDrafts, setModelDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [syncingVoices, setSyncingVoices] = useState(false);
  const [discountLoading, setDiscountLoading] = useState({ create: false, toggleId: null, sendId: null });
  const [elevenlabsVoices, setElevenlabsVoices] = useState([]);
  const [voiceAccessCatalogs, setVoiceAccessCatalogs] = useState(buildEmptyVoiceAccessCatalogs());
  const [feedback, setFeedback] = useState({ kind: "info", message: "" });
  const [menuOpen, setMenuOpen] = useState(false);
  const [secretEditors, setSecretEditors] = useState(() => buildSecretEditorState());
  const [secretDrafts, setSecretDrafts] = useState(() => buildSecretDraftState());
  const [sectionSaving, setSectionSaving] = useState({ apiKey: null, meta: false, tiktok: false });
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const enabledModels = platformSetting.enabled_models || {};
  const providerModels = platformSetting.provider_models || {};
  const workflowDefaults = platformSetting.workflow_defaults || {};
  const auroraPromptChainSettings = platformSetting.aurora_prompt_chain_settings || {};
  const billingSettings = platformSetting.billing_settings || {};
  const elevenlabsSettings = platformSetting.elevenlabs_settings || {};
  const metaApp = platformSetting.meta_app || {};
  const tiktokApp = platformSetting.tiktok_app || {};
  const metaAppConfigured = Boolean(String(metaApp.app_id || "").trim() && metaApp.app_secret_configured);
  const tiktokAppConfigured = Boolean(String(tiktokApp.client_key || "").trim() && tiktokApp.client_secret_configured);
  const metaOauthScopes = META_OAUTH_SCOPES.join(", ");
  const tiktokOauthScopes = TIKTOK_OAUTH_SCOPES.join(", ");
  const voiceAccess = elevenlabsSettings.voice_access || {};
  const providerStatus = platformSetting.provider_status || {};
  const providerHealth = platformSetting.provider_health || {};
  const creditPlans = plans.filter((plan) => plan.billing_mode === "credits");
  const displayName = auth?.name?.trim() || auth?.email?.split("@")[0] || "Account";
  const dropdownItemBaseClass =
    "flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150 focus-visible:outline-none";
  const dropdownItemActiveClass =
    "bg-[linear-gradient(135deg,rgba(34,211,238,.2),rgba(59,130,246,.14))] text-cyan-100 shadow-[inset_0_0_0_1px_rgba(34,211,238,.08),0_10px_18px_rgba(2,6,23,.16)]";
  const dropdownItemIdleClass =
    "text-slate-200 hover:translate-x-0.5 hover:bg-slate-800 hover:text-white hover:shadow-[0_10px_20px_rgba(2,6,23,.26)] focus-visible:bg-slate-800 focus-visible:text-white";
  const dropdownSuperadminClass =
    "bg-[linear-gradient(135deg,rgba(168,85,247,.16),rgba(59,130,246,.1))] text-violet-100 shadow-[inset_0_0_0_1px_rgba(168,85,247,.08),0_10px_18px_rgba(2,6,23,.16)]";
  const dropdownSignOutClass =
    "text-rose-100 hover:translate-x-0.5 hover:bg-rose-500/18 hover:text-white hover:shadow-[0_10px_20px_rgba(190,24,93,.16)] focus-visible:bg-rose-500/18 focus-visible:text-white";
  const adminMenuItems = [
    { label: "Generation", href: "/app" },
    { label: "Account", href: "/app/account" },
    { label: "Billing", href: "/app/billing" },
    { label: "Settings", href: "/app/settings" }
  ];

  useEffect(() => {
    setPlatformSetting(initialPlatformSetting);
    setPlans(initialPlans);
    setDiscountCodes(Array.isArray(initialDiscountCodes) ? initialDiscountCodes : []);
    setDiscountEmailDrafts(
      Object.fromEntries(
        (Array.isArray(initialDiscountCodes) ? initialDiscountCodes : []).map((code) => [code.id, code.recipient_email || ""])
      )
    );
    setSecretEditors(buildSecretEditorState());
    setSecretDrafts(buildSecretDraftState());
    void refreshProviderCatalog(initialPlatformSetting, { silent: true }).catch(() => {});

    if (isProviderVerified(initialPlatformSetting, "elevenlabs")) {
      void syncElevenlabsVoices(initialPlatformSetting, { silent: true }).catch(() => {});
    } else {
      setElevenlabsVoices([]);
      setVoiceAccessCatalogs(buildEmptyVoiceAccessCatalogs());
    }
  }, [initialPlatformSetting, initialPlans, initialDiscountCodes]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  const imageGenerationOptions = useMemo(
    () => includeCurrentSelection(imageModelsForProvider(workflowDefaults.image_provider, providerModels), workflowDefaults.image_model),
    [providerModels, workflowDefaults.image_model, workflowDefaults.image_provider]
  );

  const imageEditOptions = useMemo(
    () => includeCurrentSelection(imageEditModelsForProvider(workflowDefaults.image_edit_provider, providerModels), workflowDefaults.image_edit_model),
    [providerModels, workflowDefaults.image_edit_model, workflowDefaults.image_edit_provider]
  );

  const imageGenerationResolutionOptions = useMemo(
    () => workflowResolutionOptions(workflowDefaults.image_provider, workflowDefaults.image_model),
    [workflowDefaults.image_model, workflowDefaults.image_provider]
  );

  const imageEditResolutionOptions = useMemo(
    () => workflowResolutionOptions(workflowDefaults.image_edit_provider, workflowDefaults.image_edit_model),
    [workflowDefaults.image_edit_model, workflowDefaults.image_edit_provider]
  );

  const videoOptions = useMemo(
    () => includeCurrentSelection(getFalVideoModels(providerModels.fal || []), workflowDefaults.video_model),
    [providerModels.fal, workflowDefaults.video_model]
  );

  const openrouterModelOptions = useMemo(
    () => includeCurrentSelection(providerModels.openrouter || [], workflowDefaults.copy_model),
    [providerModels.openrouter, workflowDefaults.copy_model]
  );

  const auroraVisionModelOptions = useMemo(
    () => includeCurrentSelection(providerModels.openrouter || [], auroraPromptChainSettings.vision_model),
    [providerModels.openrouter, auroraPromptChainSettings.vision_model]
  );

  const auroraTextModelOptions = useMemo(
    () => includeCurrentSelection(providerModels.openrouter || [], auroraPromptChainSettings.text_model),
    [providerModels.openrouter, auroraPromptChainSettings.text_model]
  );

  const hasSavedVoiceOutsideCurrentList = Boolean(
    workflowDefaults.voice_id && !elevenlabsVoices.some((voice) => voice.voice_id === workflowDefaults.voice_id)
  );
  const voiceAccessCategories = useMemo(
    () => buildVoiceAccessCategories(voiceAccessCatalogs, voiceAccess),
    [voiceAccess, voiceAccessCatalogs]
  );

  async function savePlatformSetting(nextSetting, nextPlans = plans) {
    const response = await fetch("/admin/settings", {
      method: "PATCH",
      credentials: "same-origin",
      headers: buildJsonHeaders(),
      body: JSON.stringify({
        platform_setting: {
          ...nextSetting,
          plans: nextPlans
        }
      })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(firstError(data.errors) || data.error || "Save failed.");
    }

    return {
      message: data.message || "Settings saved.",
      platformSetting: data.platform_setting,
      plans: data.plans || nextPlans
    };
  }

  async function savePartialPlatformSetting(partialSetting) {
    const response = await fetch("/admin/settings", {
      method: "PATCH",
      credentials: "same-origin",
      headers: buildJsonHeaders(),
      body: JSON.stringify({
        platform_setting: partialSetting
      })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(firstError(data.errors) || data.error || "Save failed.");
    }

    return {
      message: data.message || "Settings saved.",
      platformSetting: data.platform_setting,
      plans: data.plans || plans
    };
  }

  async function refreshProviderCatalog(sourceSetting = platformSetting, options = {}) {
    const { silent = false } = options;
    setCatalogLoading(true);

    try {
      const response = await fetch("/api/provider-models/all", {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Catalog fetch failed.");
      }

      setProviderCatalog(data.providers || {});

      if (!silent) {
        setFeedback({ kind: "ok", message: "Provider catalog refreshed." });
      }

      return data.providers || {};
    } catch (error) {
      if (!silent) {
        setFeedback({ kind: "error", message: error.message || "Catalog fetch failed." });
      }
      throw error;
    } finally {
      setCatalogLoading(false);
    }
  }

  async function fetchElevenlabsVoices(sourceSetting, scopeOverride) {
    if (!isProviderVerified(sourceSetting, "elevenlabs")) {
      throw new Error("Save a valid ElevenLabs API key first.");
    }

    const scope = String(scopeOverride || sourceSetting.elevenlabs_settings?.voice_scope || "my_generated").trim() || "my_generated";
    const response = await fetch(`/api/elevenlabs/voices?scope=${encodeURIComponent(scope)}`, {
      credentials: "same-origin"
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Voice sync failed.");
    }

    return {
      scope: data.scope || scope,
      voices: data.voices || []
    };
  }

  async function syncElevenlabsVoices(sourceSetting, options = {}) {
    const { silent = false, persistResolvedVoice = false } = options;
    setSyncingVoices(true);

    try {
      const currentScope = String(sourceSetting?.elevenlabs_settings?.voice_scope || "my_generated").trim() || "my_generated";
      const scopes = uniqStrings([currentScope, "all"]);
      const scopedPayloads = await Promise.all(
        scopes.map(async (scope) => [scope, await fetchElevenlabsVoices(sourceSetting, scope)])
      );
      const scopeMap = Object.fromEntries(scopedPayloads);
      const currentPayload = scopeMap[currentScope] || { scope: currentScope, voices: [] };
      const voices = uniqVoiceEntries(currentPayload.voices || []);
      const allCatalogVoices = uniqVoiceEntries([...(scopeMap.all?.voices || []), ...voices]);
      const nextVoiceAccessCatalogs = buildVoiceAccessCatalogs(allCatalogVoices);

      setElevenlabsVoices(voices);
      setVoiceAccessCatalogs(nextVoiceAccessCatalogs);

      let nextSetting = {
        ...sourceSetting,
        elevenlabs_settings: {
          ...(sourceSetting.elevenlabs_settings || {}),
          voice_scope: currentPayload.scope || currentScope
        },
        workflow_defaults: {
          ...(sourceSetting.workflow_defaults || {}),
          voice_id: resolveDefaultVoiceId(sourceSetting.workflow_defaults?.voice_id, voices)
        }
      };

      if (persistResolvedVoice && nextSetting.workflow_defaults.voice_id !== sourceSetting.workflow_defaults?.voice_id) {
        const persisted = await savePlatformSetting(nextSetting, plans);
        nextSetting = persisted.platformSetting;
        setPlans(persisted.plans || plans);
      }

      setPlatformSetting(nextSetting);

      if (!silent) {
        setFeedback({
          kind: "ok",
          message: buildElevenlabsSyncMessage({
            voices,
            voiceAccessCatalogs: nextVoiceAccessCatalogs
          })
        });
      }

      return { voices, voiceAccessCatalogs: nextVoiceAccessCatalogs, nextSetting };
    } catch (error) {
      setElevenlabsVoices([]);
      setVoiceAccessCatalogs(buildEmptyVoiceAccessCatalogs());
      if (!silent) {
        setFeedback({ kind: "error", message: error.message || "Voice sync failed." });
      }
      throw error;
    } finally {
      setSyncingVoices(false);
    }
  }

  const saveSettings = async () => {
    setSaving(true);
    setFeedback({ kind: "info", message: "" });

    try {
      const persisted = await savePlatformSetting(platformSetting, plans);
      let nextSetting = persisted.platformSetting;
      let nextPlans = persisted.plans || plans;
      let nextKind = "ok";
      let nextMessage = persisted.message;

      await refreshProviderCatalog(nextSetting, { silent: true }).catch(() => {});

      if (isProviderVerified(nextSetting, "elevenlabs")) {
        try {
          const { voices, voiceAccessCatalogs: syncedCatalogs, nextSetting: syncedSetting } = await syncElevenlabsVoices(nextSetting, {
            silent: true,
            persistResolvedVoice: true
          });
          nextSetting = syncedSetting;
          nextPlans = persisted.plans || nextPlans;
          nextMessage = `${nextMessage} ${buildElevenlabsSyncMessage({
            voices,
            voiceAccessCatalogs: syncedCatalogs
          })}`.trim();
        } catch (error) {
          nextKind = "info";
          nextMessage = `${nextMessage} ElevenLabs sync failed: ${error.message || "Voice sync failed."}`.trim();
        }
      } else {
        setElevenlabsVoices([]);
        setVoiceAccessCatalogs(buildEmptyVoiceAccessCatalogs());
      }

      setPlatformSetting(nextSetting);
      setPlans(nextPlans);
      setFeedback({ kind: nextKind, message: nextMessage });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const verifyProviders = async () => {
    setVerifying(true);
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch("/admin/settings/verify", {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(firstError(data.errors) || data.error || "Provider verification failed.");
      }

      setPlatformSetting(data.platform_setting);
      setPlans(data.plans || []);
      await refreshProviderCatalog(data.platform_setting, { silent: true }).catch(() => {});

      if (isProviderVerified(data.platform_setting, "elevenlabs")) {
        try {
          const { voices, voiceAccessCatalogs: syncedCatalogs } = await syncElevenlabsVoices(data.platform_setting, { silent: true });
          setFeedback({
            kind: "ok",
            message: `${data.message || "Provider verification completed."} ${buildElevenlabsSyncMessage({
              voices,
              voiceAccessCatalogs: syncedCatalogs
            })}`.trim()
          });
        } catch (error) {
          setFeedback({
            kind: "info",
            message: `${data.message || "Provider verification completed."} ElevenLabs sync failed: ${error.message || "Voice sync failed."}`.trim()
          });
        }
      } else {
        setElevenlabsVoices([]);
        setVoiceAccessCatalogs(buildEmptyVoiceAccessCatalogs());
        setFeedback({ kind: "ok", message: data.message || "Provider verification completed." });
      }
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Provider verification failed." });
    } finally {
      setVerifying(false);
    }
  };

  const updateApiKey = (provider, value) => {
    setSecretDrafts((prev) => ({
      ...prev,
      api_keys: {
        ...(prev.api_keys || {}),
        [provider]: value
      }
    }));
  };

  const updateMetaAppSetting = (key, value) => {
    setPlatformSetting((prev) => ({
      ...prev,
      meta_app: {
        ...(prev.meta_app || {}),
        [key]: value
      }
    }));
  };

  const updateTiktokAppSetting = (key, value) => {
    setPlatformSetting((prev) => ({
      ...prev,
      tiktok_app: {
        ...(prev.tiktok_app || {}),
        [key]: value
      }
    }));
  };

  const enableWorkflowDefaultModel = (setting, provider, model) => {
    const normalizedProvider = String(provider || "").trim();
    const normalizedModel = String(model || "").trim();
    if (!normalizedProvider || !normalizedModel) return setting;

    return {
      ...setting,
      enabled_models: {
        ...(setting.enabled_models || {}),
        [normalizedProvider]: {
          ...((setting.enabled_models || {})[normalizedProvider] || {}),
          [normalizedModel]: true
        }
      }
    };
  };

  const updateWorkflowProvider = (providerKey, modelKey, provider) => {
    const normalizedProvider = provider === "wavespeed" ? "wavespeed" : "fal";
    const nextModelOptions =
      modelKey === "image_model"
        ? imageModelsForProvider(normalizedProvider, providerModels)
        : imageEditModelsForProvider(normalizedProvider, providerModels);
    const resolutionKey = modelKey === "image_model" ? "image_resolution" : "image_edit_resolution";

    setPlatformSetting((prev) => {
      const currentModel = prev.workflow_defaults?.[modelKey];
      const nextModel = nextModelOptions.includes(currentModel) ? currentModel : nextModelOptions[0] || "";
      const nextResolution = resolveWorkflowResolution(
        prev.workflow_defaults?.[resolutionKey],
        workflowResolutionOptions(normalizedProvider, nextModel)
      );
      return enableWorkflowDefaultModel(
        {
          ...prev,
          workflow_defaults: {
            ...prev.workflow_defaults,
            [providerKey]: normalizedProvider,
            [modelKey]: nextModel,
            [resolutionKey]: nextResolution
          }
        },
        normalizedProvider,
        nextModel
      );
    });
  };

  const updateWorkflowModel = (key, value) => {
    setPlatformSetting((prev) => {
      const workflowDefaults = prev.workflow_defaults || {};
      const provider =
        key === "image_model"
          ? workflowDefaults.image_provider || "fal"
          : key === "image_edit_model"
            ? workflowDefaults.image_edit_provider || "fal"
            : key === "copy_model"
              ? "openrouter"
            : key === "tts_model"
              ? "elevenlabs"
              : "fal";
      const resolutionKey =
        key === "image_model"
          ? "image_resolution"
          : key === "image_edit_model"
            ? "image_edit_resolution"
            : null;
      const nextWorkflowDefaults = {
        ...workflowDefaults,
        [key]: value
      };
      if (resolutionKey) {
        nextWorkflowDefaults[resolutionKey] = resolveWorkflowResolution(
          workflowDefaults[resolutionKey],
          workflowResolutionOptions(provider, value)
        );
      }

      return enableWorkflowDefaultModel(
        {
          ...prev,
          workflow_defaults: nextWorkflowDefaults
        },
        provider,
        value
      );
    });
  };

  const updateWorkflowResolution = (key, value) => {
    setPlatformSetting((prev) => ({
      ...prev,
      workflow_defaults: {
        ...(prev.workflow_defaults || {}),
        [key]: value
      }
    }));
  };

  const updateAuroraPromptChainSetting = (key, value) => {
    setPlatformSetting((prev) => ({
      ...prev,
      aurora_prompt_chain_settings: {
        ...(prev.aurora_prompt_chain_settings || {}),
        [key]: value
      }
    }));
  };

  const updateBillingSetting = (key, value) => {
    setPlatformSetting((prev) => ({
      ...prev,
      billing_settings: {
        ...(prev.billing_settings || {}),
        [key]: value
      }
    }));
  };

  const updatePlan = (planId, key, value) => {
    setPlans((prev) => prev.map((plan) => (plan.id === planId ? { ...plan, [key]: value } : plan)));
  };

  const updateDiscountCodeDraft = (key, value) => {
    setDiscountCodeDraft((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const updateDiscountEmailDraft = (discountCodeId, value) => {
    setDiscountEmailDrafts((prev) => ({
      ...prev,
      [discountCodeId]: value
    }));
  };

  const createDiscountCode = async () => {
    setDiscountLoading((prev) => ({ ...prev, create: true }));
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch("/admin/discount_codes", {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          discount_code: discountCodeDraft
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || firstError(data.errors) || "Discount code creation failed.");
      }

      setDiscountCodes(Array.isArray(data.discount_codes) ? data.discount_codes : []);
      setDiscountCodeDraft(buildDiscountCodeDraft());
      setDiscountEmailDrafts((prev) => ({
        ...prev,
        [data.discount_code?.id]: data.discount_code?.recipient_email || ""
      }));
      setFeedback({ kind: "ok", message: data.message || "Discount code created." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Discount code creation failed." });
    } finally {
      setDiscountLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const toggleDiscountCodeActive = async (discountCode) => {
    setDiscountLoading((prev) => ({ ...prev, toggleId: discountCode.id }));
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch(`/admin/discount_codes/${discountCode.id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          discount_code: {
            active: !discountCode.active
          }
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || firstError(data.errors) || "Discount code update failed.");
      }

      setDiscountCodes(Array.isArray(data.discount_codes) ? data.discount_codes : []);
      setFeedback({ kind: "ok", message: data.message || "Discount code updated." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Discount code update failed." });
    } finally {
      setDiscountLoading((prev) => ({ ...prev, toggleId: null }));
    }
  };

  const sendDiscountCodeInstructions = async (discountCodeId) => {
    setDiscountLoading((prev) => ({ ...prev, sendId: discountCodeId }));
    setFeedback({ kind: "info", message: "" });

    try {
      const response = await fetch(`/admin/discount_codes/${discountCodeId}/send_instructions`, {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          recipient_email: discountEmailDrafts[discountCodeId] || ""
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || firstError(data.errors) || "Instruction email failed.");
      }

      setDiscountCodes(Array.isArray(data.discount_codes) ? data.discount_codes : []);
      setDiscountEmailDrafts((prev) => ({
        ...prev,
        [discountCodeId]: data.discount_code?.recipient_email || prev[discountCodeId] || ""
      }));
      setFeedback({ kind: "ok", message: data.message || "Instruction email queued." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Instruction email failed." });
    } finally {
      setDiscountLoading((prev) => ({ ...prev, sendId: null }));
    }
  };

  const updateModelDraft = (provider, value) => {
    setModelDrafts((prev) => ({
      ...prev,
      [provider]: value
    }));
  };

  const addProviderModel = (provider) => {
    const draft = String(modelDrafts[provider] || "").trim();
    if (!draft) {
      setFeedback({ kind: "error", message: `Enter a valid slug for ${providerLabels[provider]}.` });
      return;
    }

    const currentModels = providerModels[provider] || [];
    if (currentModels.includes(draft)) {
      setFeedback({ kind: "info", message: `${draft} is already present in ${providerLabels[provider]}.` });
      return;
    }

    setPlatformSetting((prev) => ({
      ...prev,
      provider_models: {
        ...prev.provider_models,
        [provider]: [...(prev.provider_models?.[provider] || []), draft]
      },
      enabled_models: {
        ...prev.enabled_models,
        [provider]: {
          ...(prev.enabled_models?.[provider] || {}),
          [draft]: true
        }
      }
    }));

    setModelDrafts((prev) => ({
      ...prev,
      [provider]: ""
    }));
    setFeedback({ kind: "ok", message: `Model added to ${providerLabels[provider]}: ${draft}` });
  };

  const handleVoiceScopeChange = (value) => {
    const nextSetting = {
      ...platformSetting,
      elevenlabs_settings: {
        ...platformSetting.elevenlabs_settings,
        voice_scope: value
      }
    };

    setPlatformSetting(nextSetting);

    if (isProviderVerified(nextSetting, "elevenlabs")) {
      void syncElevenlabsVoices(nextSetting, { silent: true }).catch((error) => {
        setFeedback({ kind: "error", message: error.message || "Voice sync failed." });
      });
    } else {
      setElevenlabsVoices([]);
    }
  };

  const toggleVoiceAccess = (category, voiceId, checked) => {
    setPlatformSetting((prev) => {
      const currentIds = new Set(effectiveVoiceAccessIds(category, prev.elevenlabs_settings, voiceAccessCatalogs));
      if (checked) {
        currentIds.add(voiceId);
      } else {
        currentIds.delete(voiceId);
      }

      return {
        ...prev,
        elevenlabs_settings: {
          ...(prev.elevenlabs_settings || {}),
          voice_access: {
            ...((prev.elevenlabs_settings || {}).voice_access || {}),
            [category]: [...currentIds]
          }
        }
      };
    });
  };

  const setVoiceAccessCategory = (category, voiceIds) => {
    setPlatformSetting((prev) => ({
      ...prev,
      elevenlabs_settings: {
        ...(prev.elevenlabs_settings || {}),
        voice_access: {
          ...((prev.elevenlabs_settings || {}).voice_access || {}),
          [category]: uniqStrings(voiceIds)
        }
      }
    }));
  };

  const toggleModel = (provider, model, checked) => {
    setPlatformSetting((prev) => ({
      ...prev,
      enabled_models: {
        ...prev.enabled_models,
        [provider]: {
          ...(prev.enabled_models?.[provider] || {}),
          [model]: checked
        }
      }
    }));
  };

  const toggleApiKeyEditor = (provider, visible) => {
    setSecretEditors((prev) => ({
      ...prev,
      api_keys: {
        ...(prev.api_keys || {}),
        [provider]: visible
      }
    }));

    if (!visible) {
      setSecretDrafts((prev) => ({
        ...prev,
        api_keys: {
          ...(prev.api_keys || {}),
          [provider]: ""
        }
      }));
    }
  };

  const saveProviderApiKey = async (provider) => {
    const draft = String(secretDrafts.api_keys?.[provider] || "").trim();
    if (!draft) {
      setFeedback({ kind: "error", message: `Enter a new ${providerLabels[provider]} API key first.` });
      return;
    }

    setSectionSaving((prev) => ({ ...prev, apiKey: provider }));
    setFeedback({ kind: "info", message: "" });

    try {
      const persisted = await savePartialPlatformSetting({
        api_keys: {
          [provider]: draft
        }
      });
      setPlatformSetting(persisted.platformSetting);
      setPlans(persisted.plans || plans);
      toggleApiKeyEditor(provider, false);
      setFeedback({ kind: "ok", message: `${providerLabels[provider]} API key saved.` });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Save failed." });
    } finally {
      setSectionSaving((prev) => ({ ...prev, apiKey: null }));
    }
  };

  const saveMetaAppSettings = async () => {
    setSectionSaving((prev) => ({ ...prev, meta: true }));
    setFeedback({ kind: "info", message: "" });

    try {
      const payload = {
        meta_app: {
          app_id: metaApp.app_id || "",
          graph_api_version: metaApp.graph_api_version || "",
          oauth_configuration_id: metaApp.oauth_configuration_id || "",
          redirect_uri: metaApp.redirect_uri || ""
        }
      };
      const secretDraft = String(secretDrafts.meta_app_secret || "").trim();
      if (secretEditors.meta_app_secret && secretDraft) {
        payload.meta_app.app_secret = secretDraft;
      }

      const persisted = await savePartialPlatformSetting(payload);
      setPlatformSetting(persisted.platformSetting);
      setPlans(persisted.plans || plans);
      setSecretEditors((prev) => ({ ...prev, meta_app_secret: false }));
      setSecretDrafts((prev) => ({ ...prev, meta_app_secret: "" }));
      setFeedback({ kind: "ok", message: "Meta app settings saved." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Save failed." });
    } finally {
      setSectionSaving((prev) => ({ ...prev, meta: false }));
    }
  };

  const saveTiktokAppSettings = async () => {
    setSectionSaving((prev) => ({ ...prev, tiktok: true }));
    setFeedback({ kind: "info", message: "" });

    try {
      const payload = {
        tiktok_app: {
          client_key: tiktokApp.client_key || "",
          redirect_uri: tiktokApp.redirect_uri || ""
        }
      };
      const secretDraft = String(secretDrafts.tiktok_client_secret || "").trim();
      if (secretEditors.tiktok_client_secret && secretDraft) {
        payload.tiktok_app.client_secret = secretDraft;
      }

      const persisted = await savePartialPlatformSetting(payload);
      setPlatformSetting(persisted.platformSetting);
      setPlans(persisted.plans || plans);
      setSecretEditors((prev) => ({ ...prev, tiktok_client_secret: false }));
      setSecretDrafts((prev) => ({ ...prev, tiktok_client_secret: "" }));
      setFeedback({ kind: "ok", message: "TikTok app settings saved." });
    } catch (error) {
      setFeedback({ kind: "error", message: error.message || "Save failed." });
    } finally {
      setSectionSaving((prev) => ({ ...prev, tiktok: false }));
    }
  };

  return (
    <main className="min-h-screen bg-black px-6 py-8 text-white sm:px-8">
      <div className="mx-auto max-w-7xl space-y-4">
        <header className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-10 w-10 shrink-0 md:h-12 md:w-12" />
            <div>
              <h1 className="text-base font-bold tracking-tight md:text-base">
                <SparklesText textClassName="font-bold tracking-tight">UGC Persona</SparklesText>
              </h1>
              <p className="mt-0.5 text-[11px] font-medium text-slate-300 md:text-xs">Your AI influencer. Your brand. Infinite content.</p>
            </div>
          </div>

          {auth ? (
            <div className="relative shrink-0">
              <button
                ref={menuButtonRef}
                type="button"
                aria-expanded={menuOpen}
                className="inline-flex items-center gap-2 rounded-full border border-cyan-400/35 bg-slate-950/70 px-1.5 py-1.5 shadow-[0_0_0_1px_rgba(8,145,178,.12),0_10px_24px_rgba(2,6,23,.28)] transition hover:border-cyan-300/55 hover:bg-slate-900/80"
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <img src={auth.avatar_url} alt={displayName} className="h-8 w-8 rounded-full object-cover" />
                <svg
                  viewBox="0 0 20 20"
                  className={cn("h-4 w-4 text-slate-300 transition-transform", menuOpen && "rotate-180")}
                  aria-hidden
                >
                  <path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {menuOpen ? (
                <div
                  ref={menuRef}
                  className="absolute right-0 top-full z-50 mt-2 w-[250px] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950/95 p-2 shadow-[0_18px_40px_rgba(2,6,23,.55)] backdrop-blur"
                >
                  <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2.5">
                    <p className="truncate text-sm font-semibold text-slate-100">{displayName}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-400">{auth.email}</p>
                  </div>

                  <div className="mt-2 space-y-1">
                    {adminMenuItems.map((item) => (
                      <button
                        key={`menu-${item.href}`}
                        type="button"
                        className={cn(dropdownItemBaseClass, dropdownItemIdleClass)}
                        onClick={() => {
                          setMenuOpen(false);
                          router.visit(item.href);
                        }}
                      >
                        <span>{item.label}</span>
                      </button>
                    ))}

                    <button
                      type="button"
                      className={cn(dropdownItemBaseClass, dropdownSuperadminClass)}
                      onClick={() => setMenuOpen(false)}
                    >
                      <span>Superadmin</span>
                    </button>

                    <div className="my-1 h-px bg-slate-800" />

                    <button
                      type="button"
                      className={cn(dropdownItemBaseClass, dropdownSignOutClass)}
                      onClick={() => {
                        setMenuOpen(false);
                        router.delete("/users/sign_out");
                      }}
                    >
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </header>

        <header className="flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-slate-800 bg-slate-950/90 p-6 shadow-[0_24px_70px_rgba(2,6,23,.55)]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Superadmin</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Platform Settings</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
              Centralized management of provider keys, enabled models, and global defaults. Authenticated users only interact with the Rails proxy.
            </p>
            <p className="mt-3 text-xs text-slate-500">Current user: {auth?.email}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => router.visit("/app")}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
            >
              <HeaderActionIcon>
                <IconOpenApp />
              </HeaderActionIcon>
              <span>Open app</span>
            </button>
            <button
              type="button"
              onClick={() => {
                void refreshProviderCatalog(platformSetting, { silent: false }).catch(() => {});
              }}
              disabled={catalogLoading || saving || verifying}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <HeaderActionIcon>
                <IconCatalog className={cn(catalogLoading && "animate-spin")} />
              </HeaderActionIcon>
              <span>{catalogLoading ? "Refreshing..." : "Fetch catalogs"}</span>
            </button>
            <button
              type="button"
              onClick={verifyProviders}
              disabled={verifying || saving || syncingVoices}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <HeaderActionIcon>
                <IconVerify className={cn(verifying && "animate-pulse")} />
              </HeaderActionIcon>
              <span>{verifying ? "Checking..." : "Verify providers"}</span>
            </button>
            <ShimmerButton type="button" onClick={saveSettings} disabled={saving || syncingVoices || verifying}>
              <span className="inline-flex items-center gap-2">
                <HeaderActionIcon className="border-white/20 bg-white/10 text-white/95">
                  <IconSave className={cn(saving && "animate-pulse")} />
                </HeaderActionIcon>
                <span>{saving ? "Saving..." : "Save settings"}</span>
              </span>
            </ShimmerButton>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-4">
          {Object.entries(providerLabels).map(([provider, label]) => (
            <MagicCard key={provider} className={cn("bg-gradient-to-br p-5", providerMeta[provider].cardTone)}>
              <div className="flex items-start justify-between gap-3">
                <ProviderIdentity provider={provider} title={label} subtitle={providerMeta[provider].eyebrow} />
              </div>
              <p className={cn("mt-3 text-sm font-medium", providerStateColor(providerHealth[provider]))}>
                {providerStateLabel(providerHealth[provider], providerStatus[provider])}
              </p>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                {providerHealth[provider]?.message || (providerStatus[provider] ? "API key present. Verification pending." : "Missing API key.")}
              </p>
            </MagicCard>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <MagicCard>
            <h2 className="section-title">Workflow Defaults</h2>
            <p className="mt-1 text-sm text-slate-400">Locked defaults for authenticated users. Providers and models are applied from this panel and are not user-editable in the app.</p>
            <div className="mt-4 grid gap-4">
              <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-200">Providers</h3>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="label-ui">
                    Step 1 provider
                    <select
                      className="input-ui"
                      value={workflowDefaults.image_provider || "fal"}
                      onChange={(event) => updateWorkflowProvider("image_provider", "image_model", event.target.value)}
                    >
                      {imageProviders.map((provider) => (
                        <option key={provider.value} value={provider.value}>
                          {provider.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="label-ui">
                    Step 2 provider
                    <select
                      className="input-ui"
                      value={workflowDefaults.image_edit_provider || "fal"}
                      onChange={(event) => updateWorkflowProvider("image_edit_provider", "image_edit_model", event.target.value)}
                    >
                      {imageProviders.map((provider) => (
                        <option key={provider.value} value={provider.value}>
                          {provider.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-200">Models</h3>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="label-ui">
                    Step 1 model
                    <select
                      className="input-ui"
                      value={workflowDefaults.image_model || ""}
                      onChange={(event) => updateWorkflowModel("image_model", event.target.value)}
                    >
                      {imageGenerationOptions.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="label-ui">
                    Step 1 resolution
                    <select
                      className="input-ui"
                      value={resolveWorkflowResolution(workflowDefaults.image_resolution, imageGenerationResolutionOptions)}
                      onChange={(event) => updateWorkflowResolution("image_resolution", event.target.value)}
                      disabled={imageGenerationResolutionOptions.length === 0}
                    >
                      {imageGenerationResolutionOptions.length === 0 ? (
                        <option value="">Managed by provider</option>
                      ) : (
                        imageGenerationResolutionOptions.map((resolution) => (
                          <option key={resolution} value={resolution}>
                            {resolution}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  <label className="label-ui">
                    Step 2 model
                    <select
                      className="input-ui"
                      value={workflowDefaults.image_edit_model || ""}
                      onChange={(event) => updateWorkflowModel("image_edit_model", event.target.value)}
                    >
                      {imageEditOptions.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="label-ui">
                    Step 2 resolution
                    <select
                      className="input-ui"
                      value={resolveWorkflowResolution(workflowDefaults.image_edit_resolution, imageEditResolutionOptions)}
                      onChange={(event) => updateWorkflowResolution("image_edit_resolution", event.target.value)}
                      disabled={imageEditResolutionOptions.length === 0}
                    >
                      {imageEditResolutionOptions.length === 0 ? (
                        <option value="">Managed by provider</option>
                      ) : (
                        imageEditResolutionOptions.map((resolution) => (
                          <option key={resolution} value={resolution}>
                            {resolution}
                          </option>
                        ))
                      )}
                    </select>
                  </label>

                  <label className="label-ui">
                    Step 3 model
                    <select
                      className="input-ui"
                      value={workflowDefaults.copy_model || ""}
                      onChange={(event) => updateWorkflowModel("copy_model", event.target.value)}
                    >
                      {openrouterModelOptions.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="label-ui">
                    Step 5 model
                    <select
                      className="input-ui"
                      value={workflowDefaults.video_model || ""}
                      onChange={(event) => updateWorkflowModel("video_model", event.target.value)}
                    >
                      {videoOptions.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </MagicCard>

          <MagicCard>
            <h2 className="section-title">Aurora Prompt Chain</h2>
            <p className="mt-1 text-sm text-slate-400">
              Configures the optional Step 5 Optimize Prompt workflow. The vision model reads the image, while the text model handles user intent, script analysis, and final Aurora synthesis.
            </p>
            <div className="mt-4 grid gap-3">
              <label className="flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-cyan-400 focus:ring-cyan-400"
                  checked={Boolean(auroraPromptChainSettings.enabled)}
                  onChange={(event) => updateAuroraPromptChainSetting("enabled", event.target.checked)}
                />
                <span className="space-y-1">
                  <span className="block text-sm font-semibold text-slate-100">Enable Step 5 prompt optimizer</span>
                  <span className="block text-xs leading-5 text-slate-400">
                    When enabled, the Optimize Prompt block is available in Step 5 for superadmins only.
                  </span>
                </span>
              </label>

              <label className="label-ui">
                Vision analysis model
                <select
                  className="input-ui"
                  value={auroraPromptChainSettings.vision_model || ""}
                  onChange={(event) => updateAuroraPromptChainSetting("vision_model", event.target.value)}
                >
                  {auroraVisionModelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>

              <label className="label-ui">
                Text analysis + synthesis model
                <select
                  className="input-ui"
                  value={auroraPromptChainSettings.text_model || ""}
                  onChange={(event) => updateAuroraPromptChainSetting("text_model", event.target.value)}
                >
                  {auroraTextModelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">
                {!providerStatus.openrouter
                  ? "Save an OpenRouter API key first. The Aurora prompt chain depends on OpenRouter for vision analysis, text analysis, and final synthesis."
                  : !isProviderVerified(platformSetting, "openrouter")
                    ? providerHealth.openrouter?.message || "Verify OpenRouter before using the Aurora prompt chain."
                    : "These models are used by the optional Optimize Prompt flow in Step 5. Save after changing them."}
              </div>
            </div>
          </MagicCard>

          <MagicCard>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-fuchsia-400/25 bg-slate-950/80 shadow-[0_0_30px_rgba(217,70,239,0.12)]">
                  <MetaIcon className="h-6 w-6" />
                </div>
                <h2 className="section-title">Meta Publishing App</h2>
                <p className="mt-1 text-sm text-slate-400">
                  App-level configuration used for Meta OAuth and Instagram Reel publishing. Secret values stay write-only and can be updated on demand.
                </p>
              </div>
              <button
                type="button"
                onClick={saveMetaAppSettings}
                disabled={sectionSaving.meta || saving || verifying || syncingVoices}
                className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sectionSaving.meta ? "Saving..." : "Save Meta app"}
              </button>
            </div>
            <div className="mt-4 grid gap-3">
              <label className="label-ui">
                Meta App ID
                <input
                  className="input-ui font-mono text-xs"
                  type="text"
                  value={metaApp.app_id || ""}
                  onChange={(event) => updateMetaAppSetting("app_id", event.target.value)}
                  placeholder="123456789012345"
                />
              </label>

              <label className="label-ui">
                Meta App Secret
                <div className="mt-1 space-y-3 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-slate-300">
                      {metaApp.app_secret_configured ? "Secret configured." : "No secret saved yet."}
                    </p>
                    <div className="flex gap-2">
                      {secretEditors.meta_app_secret ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSecretEditors((prev) => ({ ...prev, meta_app_secret: false }));
                            setSecretDrafts((prev) => ({ ...prev, meta_app_secret: "" }));
                          }}
                          className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                        >
                          Cancel
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setSecretEditors((prev) => ({ ...prev, meta_app_secret: !prev.meta_app_secret }))}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                      >
                        {secretEditors.meta_app_secret ? "Hide input" : metaApp.app_secret_configured ? "Edit secret" : "Add secret"}
                      </button>
                    </div>
                  </div>

                  {secretEditors.meta_app_secret ? (
                    <input
                      className="input-ui font-mono text-xs"
                      type="password"
                      value={secretDrafts.meta_app_secret}
                      onChange={(event) => setSecretDrafts((prev) => ({ ...prev, meta_app_secret: event.target.value }))}
                      placeholder={metaApp.app_secret_configured ? "Enter a new Meta app secret to replace the current one" : "Meta app secret"}
                    />
                  ) : null}
                </div>
              </label>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="label-ui">
                  Graph API version
                  <input
                    className="input-ui font-mono text-xs"
                    type="text"
                    value={metaApp.graph_api_version || "v23.0"}
                    onChange={(event) => updateMetaAppSetting("graph_api_version", event.target.value)}
                    placeholder="v23.0"
                  />
                </label>

                <label className="label-ui">
                  Login configuration ID
                  <input
                    className="input-ui font-mono text-xs"
                    type="text"
                    value={metaApp.oauth_configuration_id || ""}
                    onChange={(event) => updateMetaAppSetting("oauth_configuration_id", event.target.value)}
                    placeholder="Optional but recommended for Facebook Login for Business"
                  />
                </label>

                <label className="label-ui">
                  OAuth redirect URI
                  <input
                    className="input-ui font-mono text-xs"
                    type="text"
                    value={metaApp.redirect_uri || ""}
                    onChange={(event) => updateMetaAppSetting("redirect_uri", event.target.value)}
                    placeholder="https://yourdomain.com/auth/meta/callback"
                  />
                </label>
              </div>

              <div className="label-ui">
                OAuth scopes
                <div className="mt-1 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                  <p className="font-mono text-xs leading-6 text-slate-200">{metaOauthScopes}</p>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Managed automatically by UGC Persona. When a Login configuration ID is set, Meta uses that configuration and these fallback scopes are not sent in the OAuth URL.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">
                <p className="font-semibold text-slate-100">
                  {metaAppConfigured ? "Meta publishing is configured." : "Meta publishing is not configured yet."}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-400">
                  Default callback: <span className="font-mono text-slate-200">/auth/meta/callback</span>. For Facebook Login for Business, paste the Login configuration ID from Meta and leave the redirect URI empty to use the current app domain automatically.
                </p>
              </div>
            </div>
          </MagicCard>

          <MagicCard>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-400/25 bg-slate-950/80 shadow-[0_0_30px_rgba(0,242,234,0.12)]">
                  <TikTokMarkIcon className="h-6 w-6" />
                </div>
                <h2 className="section-title">TikTok Publishing App</h2>
                <p className="mt-1 text-sm text-slate-400">
                  App-level configuration used for TikTok OAuth and direct video posting. Secret values stay write-only and can be updated on demand.
                </p>
              </div>
              <button
                type="button"
                onClick={saveTiktokAppSettings}
                disabled={sectionSaving.tiktok || saving || verifying || syncingVoices}
                className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sectionSaving.tiktok ? "Saving..." : "Save TikTok app"}
              </button>
            </div>
            <div className="mt-4 grid gap-3">
              <label className="label-ui">
                TikTok Client Key
                <input
                  className="input-ui font-mono text-xs"
                  type="text"
                  value={tiktokApp.client_key || ""}
                  onChange={(event) => updateTiktokAppSetting("client_key", event.target.value)}
                  placeholder="TikTok client key"
                />
              </label>

              <label className="label-ui">
                TikTok Client Secret
                <div className="mt-1 space-y-3 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-slate-300">
                      {tiktokApp.client_secret_configured ? "Secret configured." : "No secret saved yet."}
                    </p>
                    <div className="flex gap-2">
                      {secretEditors.tiktok_client_secret ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSecretEditors((prev) => ({ ...prev, tiktok_client_secret: false }));
                            setSecretDrafts((prev) => ({ ...prev, tiktok_client_secret: "" }));
                          }}
                          className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                        >
                          Cancel
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setSecretEditors((prev) => ({ ...prev, tiktok_client_secret: !prev.tiktok_client_secret }))}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                      >
                        {secretEditors.tiktok_client_secret ? "Hide input" : tiktokApp.client_secret_configured ? "Edit secret" : "Add secret"}
                      </button>
                    </div>
                  </div>

                  {secretEditors.tiktok_client_secret ? (
                    <input
                      className="input-ui font-mono text-xs"
                      type="password"
                      value={secretDrafts.tiktok_client_secret}
                      onChange={(event) => setSecretDrafts((prev) => ({ ...prev, tiktok_client_secret: event.target.value }))}
                      placeholder={tiktokApp.client_secret_configured ? "Enter a new TikTok client secret to replace the current one" : "TikTok client secret"}
                    />
                  ) : null}
                </div>
              </label>

              <label className="label-ui">
                OAuth redirect URI
                <input
                  className="input-ui font-mono text-xs"
                  type="text"
                  value={tiktokApp.redirect_uri || ""}
                  onChange={(event) => updateTiktokAppSetting("redirect_uri", event.target.value)}
                  placeholder="https://yourdomain.com/auth/tiktok/callback"
                />
              </label>

              <div className="label-ui">
                OAuth scopes
                <div className="mt-1 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                  <p className="font-mono text-xs leading-6 text-slate-200">{tiktokOauthScopes}</p>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Managed automatically by UGC Persona. These scopes cover account discovery and direct posting for the connected TikTok user.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">
                <p className="font-semibold text-slate-100">
                  {tiktokAppConfigured ? "TikTok publishing is configured." : "TikTok publishing is not configured yet."}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-400">
                  Default callback: <span className="font-mono text-slate-200">/auth/tiktok/callback</span>. Leave the redirect URI empty to use the current app domain automatically.
                </p>
              </div>
            </div>
          </MagicCard>

          <MagicCard className="lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="section-title">Discount Codes</h2>
                <p className="mt-1 text-sm text-slate-400">
                  Open the dedicated superadmin panel to create ambassador access codes, Stripe discounts, free-month offers, and send the instructions by email.
                </p>
              </div>
              <button
                type="button"
                onClick={() => router.visit("/admin/discount_codes")}
                className="inline-flex items-center justify-center rounded-xl bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-4 py-2.5 text-sm font-semibold text-slate-50 shadow-[0_14px_32px_rgba(2,6,23,.32)] transition hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))]"
              >
                Open discount codes panel
              </button>
            </div>
          </MagicCard>

          <MagicCard>
            <h2 className="section-title">Credits & Billing Rules</h2>
            <p className="mt-1 text-sm text-slate-400">
              Global cost rules used by the credit engine. Plans and pricing stay separate from these operation costs.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="label-ui">
                Image generation cost
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  step="0.5"
                  value={billingSettings.image_cost ?? "1"}
                  onChange={(event) => updateBillingSetting("image_cost", event.target.value)}
                />
              </label>

              <label className="label-ui">
                Copy generation cost
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  step="0.5"
                  value={billingSettings.copy_cost ?? "0.5"}
                  onChange={(event) => updateBillingSetting("copy_cost", event.target.value)}
                />
              </label>

              <label className="label-ui">
                Voice generation cost
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  step="0.5"
                  value={billingSettings.tts_cost ?? "1"}
                  onChange={(event) => updateBillingSetting("tts_cost", event.target.value)}
                />
              </label>

              <label className="label-ui">
                TTS optimization cost
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  step="0.5"
                  value={billingSettings.tts_optimization_cost ?? "0.5"}
                  onChange={(event) => updateBillingSetting("tts_optimization_cost", event.target.value)}
                />
              </label>

              <label className="label-ui">
                Video credits per billed second
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  step="0.5"
                  value={billingSettings.video_cost_per_second ?? "2"}
                  onChange={(event) => updateBillingSetting("video_cost_per_second", event.target.value)}
                />
              </label>

              <label className="label-ui">
                Minimum video seconds
                <input
                  className="input-ui"
                  type="number"
                  min="1"
                  step="1"
                  value={billingSettings.video_min_seconds ?? "5"}
                  onChange={(event) => updateBillingSetting("video_min_seconds", event.target.value)}
                />
              </label>

              <label className="label-ui">
                Maximum video seconds
                <input
                  className="input-ui"
                  type="number"
                  min="1"
                  step="1"
                  value={billingSettings.video_max_seconds ?? "30"}
                  onChange={(event) => updateBillingSetting("video_max_seconds", event.target.value)}
                />
              </label>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300">
                <p className="font-semibold text-slate-100">Current formula</p>
                <p className="mt-1">Video credits = billed seconds × {billingSettings.video_cost_per_second ?? "2"}</p>
                <p className="mt-2 text-xs leading-5 text-slate-500">Billed seconds are rounded up from the actual final duration.</p>
              </div>
            </div>
          </MagicCard>

          <MagicCard>
            <h2 className="section-title">API Keys</h2>
            <p className="mt-1 text-sm text-slate-400">Stored in the Rails backend with Active Record encryption and verified server-side. Existing keys stay hidden unless you explicitly replace them.</p>
            <div className="mt-4 grid gap-3">
              {Object.entries(providerLabels).map(([provider, label]) => (
                <div key={provider} className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <span className="flex items-center gap-3">
                      <ProviderLogo provider={provider} className="size-9 rounded-xl" />
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-slate-100">{label} API key</span>
                        <span className="text-[11px] font-normal uppercase tracking-[0.16em] text-slate-500">
                          {providerMeta[provider].eyebrow}
                        </span>
                      </span>
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn("rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]", providerStatus[provider] ? "border border-emerald-500/50 bg-emerald-500/10 text-emerald-200" : "border border-slate-700 bg-slate-900 text-slate-300")}>
                        {providerStatus[provider] ? "Configured" : "Missing"}
                      </span>
                      {secretEditors.api_keys?.[provider] ? (
                        <button
                          type="button"
                          onClick={() => toggleApiKeyEditor(provider, false)}
                          className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                        >
                          Cancel
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => toggleApiKeyEditor(provider, !secretEditors.api_keys?.[provider])}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                      >
                        {secretEditors.api_keys?.[provider] ? "Hide input" : providerStatus[provider] ? "Edit key" : "Add key"}
                      </button>
                    </div>
                  </div>

                  {secretEditors.api_keys?.[provider] ? (
                    <div className="mt-3 flex flex-col gap-2 md:flex-row">
                      <input
                        className="input-ui min-w-0 font-mono text-xs"
                        type="password"
                        value={secretDrafts.api_keys?.[provider] || ""}
                        onChange={(event) => updateApiKey(provider, event.target.value)}
                        placeholder={providerStatus[provider] ? `Enter a new ${label} API key` : `${label} API key`}
                      />
                      <button
                        type="button"
                        onClick={() => saveProviderApiKey(provider)}
                        disabled={sectionSaving.apiKey === provider}
                        className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {sectionSaving.apiKey === provider ? "Saving..." : "Save key"}
                      </button>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-300">
                      {providerStatus[provider] ? "A key is already saved for this provider." : "No key saved for this provider yet."}
                    </p>
                  )}

                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    {providerHealth[provider]?.message || "Save a new key only when you need to add or replace it."}
                  </p>
                </div>
              ))}
            </div>
          </MagicCard>

          <MagicCard className="xl:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-700 bg-slate-950/80 px-2 shadow-[0_0_30px_rgba(148,163,184,0.10)]">
                  <ElevenLabsLogo className="h-5 w-auto" />
                </div>
                <h2 className="section-title">ElevenLabs Defaults</h2>
                <p className="mt-1 text-sm text-slate-400">Global defaults used by the TTS proxy for authenticated users.</p>
              </div>

              <button
                type="button"
                onClick={() => {
                  void syncElevenlabsVoices(platformSetting, { silent: false }).catch(() => {});
                }}
                disabled={syncingVoices || !isProviderVerified(platformSetting, "elevenlabs")}
                className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {syncingVoices ? "Syncing voices..." : "Sync account voices"}
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="label-ui">
                Voice scope
                <select
                  className="input-ui"
                  value={elevenlabsSettings.voice_scope || "my_generated"}
                  onChange={(event) => handleVoiceScopeChange(event.target.value)}
                >
                  <option value="my_generated">My Voices - Generated</option>
                  <option value="my">My Voices - All</option>
                  <option value="my_cloned">My Voices - Cloned</option>
                  <option value="my_professional">My Voices - Professional</option>
                  <option value="all">All account voices</option>
                </select>
              </label>

              <label className="label-ui">
                Default voice
                <select
                  className="input-ui"
                  value={workflowDefaults.voice_id || ""}
                  disabled={!isProviderVerified(platformSetting, "elevenlabs") || syncingVoices}
                  onChange={(event) => updateWorkflowDefault("voice_id", event.target.value)}
                >
                  <option value="">
                    {!providerStatus.elevenlabs
                      ? "Save an ElevenLabs API key first"
                      : !isProviderVerified(platformSetting, "elevenlabs")
                        ? "Verify ElevenLabs before loading voices"
                        : syncingVoices
                          ? "Syncing voices..."
                          : "Select a default voice"}
                  </option>
                  {hasSavedVoiceOutsideCurrentList ? (
                    <option value={workflowDefaults.voice_id}>Saved voice ({workflowDefaults.voice_id})</option>
                  ) : null}
                  {elevenlabsVoices.map((voice) => (
                    <option key={voice.voice_id} value={voice.voice_id}>
                      {formatVoiceOptionLabel(voice)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="label-ui">
                Default TTS model
                <select
                  className="input-ui"
                  value={workflowDefaults.tts_model || ""}
                  onChange={(event) => updateWorkflowDefault("tts_model", event.target.value)}
                >
                  {includeCurrentSelection(providerModels.elevenlabs || [], workflowDefaults.tts_model).map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>

              <label className="label-ui">
                Stability
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={workflowDefaults.stability || "0.5"}
                  onChange={(event) => updateWorkflowDefault("stability", event.target.value)}
                />
              </label>

              <label className="label-ui">
                Similarity Boost
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={workflowDefaults.similarity_boost || "0.8"}
                  onChange={(event) => updateWorkflowDefault("similarity_boost", event.target.value)}
                />
              </label>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm text-slate-300 md:col-span-2">
                {!providerStatus.elevenlabs
                  ? "Add and save the ElevenLabs API key to load account voices here."
                  : !isProviderVerified(platformSetting, "elevenlabs")
                    ? providerHealth.elevenlabs?.message || "Verify the ElevenLabs key before syncing account voices."
                    : syncingVoices
                      ? "Syncing account voices from ElevenLabs..."
                      : elevenlabsVoices.length
                        ? `Account voices loaded: ${elevenlabsVoices.length}. Save after choosing the default voice.`
                        : "No voices available for the selected scope. Try another scope or sync again after saving the API key."}
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-4 text-sm text-slate-300 md:col-span-2">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-100">Voice access for regular users</p>
                    <p className="mt-1 max-w-3xl text-xs leading-5 text-slate-400">
                      Superadmins always keep access to every ElevenLabs voice. The selections below control which synced ElevenLabs voice categories are available to regular users inside the app.
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  {voiceAccessCategories.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-950/40 px-4 py-5 text-xs leading-5 text-slate-500 lg:col-span-2">
                      {!providerStatus.elevenlabs
                        ? "Save the ElevenLabs API key first."
                        : !isProviderVerified(platformSetting, "elevenlabs")
                          ? "Verify ElevenLabs before loading voice access lists."
                          : "No syncable voice categories were found in this ElevenLabs account yet."}
                    </div>
                  ) : voiceAccessCategories.map((category) => {
                    const voices = voiceAccessCatalogs[category.key] || [];
                    const selectedIds = effectiveVoiceAccessIds(category.key, elevenlabsSettings, voiceAccessCatalogs);
                    const explicitlyConfigured = hasExplicitVoiceAccessCategory(category.key, voiceAccess);
                    const allVoiceIds = voices.map((voice) => voice.voice_id);

                    return (
                      <div key={category.key} className="rounded-2xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-100">{category.title}</p>
                            <p className="mt-1 text-xs leading-5 text-slate-400">{category.description}</p>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={!isProviderVerified(platformSetting, "elevenlabs") || syncingVoices || voices.length === 0}
                              onClick={() => setVoiceAccessCategory(category.key, allVoiceIds)}
                              className="rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Select all
                            </button>
                            <button
                              type="button"
                              disabled={!isProviderVerified(platformSetting, "elevenlabs") || syncingVoices}
                              onClick={() => setVoiceAccessCategory(category.key, [])}
                              className="rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 max-h-64 space-y-2 overflow-auto pr-1">
                          {voices.length === 0 ? (
                            <p className="text-xs leading-5 text-slate-500">
                              {!providerStatus.elevenlabs
                                ? "Save the ElevenLabs API key first."
                                : !isProviderVerified(platformSetting, "elevenlabs")
                                  ? "Verify ElevenLabs before loading voice access lists."
                                  : `No ${category.title.toLowerCase()} voices found in this ElevenLabs account.`}
                            </p>
                          ) : (
                            voices.map((voice) => (
                              <label
                                key={voice.voice_id}
                                className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2 text-xs text-slate-300"
                              >
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={selectedIds.includes(voice.voice_id)}
                                  disabled={!isProviderVerified(platformSetting, "elevenlabs") || syncingVoices}
                                  onChange={(event) => toggleVoiceAccess(category.key, voice.voice_id, event.target.checked)}
                                />
                                <span className="min-w-0">
                                  <span className="block font-semibold text-slate-100">{voice.name || voice.voice_id}</span>
                                  <span className="mt-1 block break-all text-[11px] leading-5 text-slate-500">
                                    {formatVoiceOptionLabel(voice) || voice.voice_id}
                                  </span>
                                </span>
                              </label>
                            ))
                          )}
                        </div>

                        <p className="mt-3 text-xs leading-5 text-slate-500">
                          {voices.length === 0
                            ? "Nothing to configure yet for this category."
                            : explicitlyConfigured
                              ? `${selectedIds.length} ${selectedIds.length === 1 ? "voice" : "voices"} approved for regular users.`
                              : "Currently unrestricted: all account voices in this category remain available to regular users until you save a specific selection."}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </MagicCard>

          <MagicCard className="xl:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="section-title">Plans</h2>
                <p className="mt-1 text-sm text-slate-400">
                  Monthly plans shown in the homepage pricing section. Update credits, pricing, Stripe references, and marketing copy here.
                </p>
              </div>
              <p className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs text-slate-400">
                Homepage sync is automatic after save.
              </p>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {plans.map((plan) => (
                <div key={plan.id || plan.slug} className="rounded-3xl border border-slate-800 bg-slate-950/45 p-5">
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="label-ui">
                      Name
                      <input
                        className="input-ui"
                        type="text"
                        value={plan.name || ""}
                        onChange={(event) => updatePlan(plan.id, "name", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Slug
                      <input
                        className="input-ui font-mono text-xs"
                        type="text"
                        value={plan.slug || ""}
                        onChange={(event) => updatePlan(plan.id, "slug", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Billing mode
                      <select
                        className="input-ui"
                        value={plan.billing_mode || "credits"}
                        onChange={(event) => updatePlan(plan.id, "billing_mode", event.target.value)}
                      >
                        <option value="credits">Credits</option>
                        <option value="byo_api">Bring Your Own API</option>
                      </select>
                    </label>

                    <label className="label-ui">
                      Price per month
                      <input
                        className="input-ui"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formatPlanPriceInput(plan.price_cents)}
                        onChange={(event) => updatePlan(plan.id, "price_cents", priceInputToCents(event.target.value))}
                      />
                    </label>

                    <label className="label-ui">
                      Monthly credits
                      <input
                        className="input-ui"
                        type="number"
                        min="0"
                        step="1"
                        disabled={plan.billing_mode === "byo_api"}
                        value={plan.monthly_credits ?? 0}
                        onChange={(event) => updatePlan(plan.id, "monthly_credits", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Sort order
                      <input
                        className="input-ui"
                        type="number"
                        min="0"
                        step="1"
                        value={plan.sort_order ?? 0}
                        onChange={(event) => updatePlan(plan.id, "sort_order", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Badge label
                      <input
                        className="input-ui"
                        type="text"
                        value={plan.badge_label || ""}
                        onChange={(event) => updatePlan(plan.id, "badge_label", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Eyebrow
                      <input
                        className="input-ui"
                        type="text"
                        value={plan.eyebrow || ""}
                        onChange={(event) => updatePlan(plan.id, "eyebrow", event.target.value)}
                      />
                    </label>

                    <label className="label-ui md:col-span-2">
                      Tagline
                      <input
                        className="input-ui"
                        type="text"
                        value={plan.tagline || ""}
                        onChange={(event) => updatePlan(plan.id, "tagline", event.target.value)}
                      />
                    </label>

                    <label className="label-ui md:col-span-2">
                      Description
                      <textarea
                        className="input-ui min-h-28"
                        value={plan.description || ""}
                        onChange={(event) => updatePlan(plan.id, "description", event.target.value)}
                      />
                    </label>

                    <label className="label-ui md:col-span-2">
                      Marketing features
                      <textarea
                        className="input-ui min-h-32"
                        value={Array.isArray(plan.marketing_features) ? plan.marketing_features.join("\n") : ""}
                        onChange={(event) => updatePlan(plan.id, "marketing_features", linesToArray(event.target.value))}
                      />
                    </label>

                    <label className="label-ui">
                      CTA label
                      <input
                        className="input-ui"
                        type="text"
                        value={plan.cta_label || ""}
                        onChange={(event) => updatePlan(plan.id, "cta_label", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Contact email
                      <input
                        className="input-ui"
                        type="email"
                        value={plan.contact_email || ""}
                        onChange={(event) => updatePlan(plan.id, "contact_email", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Stripe product id
                      <input
                        className="input-ui font-mono text-xs"
                        type="text"
                        value={plan.stripe_product_id || ""}
                        onChange={(event) => updatePlan(plan.id, "stripe_product_id", event.target.value)}
                      />
                    </label>

                    <label className="label-ui">
                      Stripe price id
                      <input
                        className="input-ui font-mono text-xs"
                        type="text"
                        value={plan.stripe_price_id || ""}
                        onChange={(event) => updatePlan(plan.id, "stripe_price_id", event.target.value)}
                      />
                    </label>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-300">
                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={Boolean(plan.active)}
                        onChange={(event) => updatePlan(plan.id, "active", event.target.checked)}
                      />
                      <span>Active</span>
                    </label>

                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={Boolean(plan.visible_on_homepage)}
                        onChange={(event) => updatePlan(plan.id, "visible_on_homepage", event.target.checked)}
                      />
                      <span>Visible on homepage</span>
                    </label>

                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={Boolean(plan.popular)}
                        onChange={(event) => updatePlan(plan.id, "popular", event.target.checked)}
                      />
                      <span>Popular</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </MagicCard>
        </div>

        <section className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Model Toggle</h2>
              <p className="mt-1 text-sm text-slate-400">
                Add new slugs per provider and enable or disable the models exposed to the authenticated frontend.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Live suggestions: OpenRouter, Wavespeed, and ElevenLabs come from remote fetches. fal.ai uses the local curated catalog.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Object.entries(providerLabels).map(([provider, label]) => {
              const suggestions = providerSuggestionOptions(provider, providerCatalog, providerModels);
              const catalogInfo = providerCatalog[provider] || {};

              return (
                <MagicCard key={provider} className={cn("bg-gradient-to-br p-5", providerMeta[provider].cardTone)}>
                  <div className="flex items-start justify-between gap-3">
                    <ProviderIdentity provider={provider} title={label} subtitle={`Source: ${catalogInfo.source || "saved"}`} />
                    <span className={cn("rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]", providerStateBadge(providerHealth[provider]))}>
                      {providerStateLabel(providerHealth[provider], providerStatus[provider])}
                    </span>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <input
                      list={`provider-models-${provider}`}
                      className="input-ui min-w-0 text-xs"
                      type="text"
                      value={modelDrafts[provider] || ""}
                      placeholder={providerPlaceholder(provider)}
                      onChange={(event) => updateModelDraft(provider, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addProviderModel(provider);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addProviderModel(provider)}
                      className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
                    >
                      Add
                    </button>
                    <datalist id={`provider-models-${provider}`}>
                      {suggestions.map((model) => (
                        <option key={model} value={model} />
                      ))}
                    </datalist>
                  </div>

                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    {catalogInfo.warning || `Saved models: ${(providerModels[provider] || []).length}`}
                  </p>

                  <div className="mt-4 max-h-80 space-y-2 overflow-auto pr-1">
                    {(providerModels[provider] || []).map((model) => (
                      <label
                        key={model}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2 text-xs text-slate-300"
                      >
                        <span className="break-all">{model}</span>
                        <input
                          type="checkbox"
                          checked={enabledModels?.[provider]?.[model] !== false}
                          onChange={(event) => toggleModel(provider, model, event.target.checked)}
                        />
                      </label>
                    ))}
                  </div>
                </MagicCard>
              );
            })}
          </div>
        </section>

        {feedback.message ? (
          <div
            className={cn(
              "relative rounded-2xl border px-4 py-3 pr-12 text-sm",
              feedback.kind === "ok"
                ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-100"
                : feedback.kind === "error"
                  ? "border-red-500/60 bg-red-500/10 text-red-100"
                  : "border-cyan-500/60 bg-cyan-500/10 text-cyan-100"
            )}
          >
            {feedback.message}
            <button
              type="button"
              onClick={() => setFeedback({ kind: "info", message: "" })}
              className={cn(
                "absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md border bg-black/15 transition hover:bg-black/25",
                feedback.kind === "ok"
                  ? "border-emerald-200/20 text-emerald-100"
                  : feedback.kind === "error"
                    ? "border-red-200/20 text-red-100"
                    : "border-cyan-200/20 text-cyan-100"
              )}
              aria-label="Dismiss"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-4 w-4">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>
    </main>
  );
}

function TikTokMarkIcon({ className }) {
  return (
    <svg viewBox="-0.32296740998066475 -3.1283528999801873 42.68446958125966 42.128352899980186" className={className} aria-hidden>
      <g fill="none">
        <path
          d="M14 15.599v-1.486A13.1 13.1 0 0 0 12.337 14C5.535 14 0 19.18 0 25.547 0 29.452 2.086 32.91 5.267 35c-2.13-2.132-3.315-4.942-3.313-7.861 0-6.276 5.377-11.394 12.046-11.54"
          fill="#00f2ea"
        />
        <path
          d="M14.327 32c2.876 0 5.221-2.273 5.328-5.107l.01-25.292h4.65A8.72 8.72 0 0 1 24.164 0h-6.35l-.011 25.293c-.106 2.832-2.453 5.105-5.328 5.105a5.329 5.329 0 0 1-2.476-.61A5.34 5.34 0 0 0 14.327 32m18.672-21.814V8.78a8.818 8.818 0 0 1-4.81-1.421A8.85 8.85 0 0 0 33 10.186"
          fill="#00f2ea"
        />
        <path
          d="M28 7.718A8.63 8.63 0 0 1 25.832 2h-1.697A8.735 8.735 0 0 0 28 7.718M12.325 20.065c-2.94.004-5.322 2.361-5.325 5.27A5.267 5.267 0 0 0 9.854 30a5.2 5.2 0 0 1-1.008-3.073c.003-2.91 2.385-5.268 5.325-5.271.55 0 1.075.09 1.572.244v-6.4a11.72 11.72 0 0 0-1.572-.114c-.092 0-.183.006-.274.007v4.916a5.286 5.286 0 0 0-1.572-.244"
          fill="#ff004f"
        />
        <path
          d="M32.153 11v4.884a15.15 15.15 0 0 1-8.813-2.811V25.84c0 6.377-5.23 11.565-11.658 11.565-2.485 0-4.789-.778-6.682-2.097A11.67 11.67 0 0 0 13.528 39c6.429 0 11.659-5.188 11.659-11.564V14.668A15.15 15.15 0 0 0 34 17.478v-6.283A8.87 8.87 0 0 1 32.153 11"
          fill="#ff004f"
        />
        <path
          d="M23.979 25.42V12.632A15.741 15.741 0 0 0 33 15.448v-4.89a9.083 9.083 0 0 1-4.912-2.82C26.016 6.431 24.586 4.358 24.132 2h-4.747l-.01 25.215c-.11 2.824-2.505 5.09-5.44 5.09-1.754-.002-3.398-.822-4.42-2.204-1.794-.913-2.919-2.716-2.92-4.682.003-2.92 2.44-5.285 5.45-5.289.56 0 1.098.09 1.608.245v-4.933C7.202 15.589 2 20.722 2 27.016c0 3.045 1.219 5.816 3.205 7.885A12.115 12.115 0 0 0 12.045 37c6.58 0 11.934-5.195 11.934-11.58"
          fill="#fff"
        />
      </g>
    </svg>
  );
}

function MetaIcon({ className }) {
  return (
    <svg viewBox="0 0 290 191" className={className} fill="none" aria-hidden>
      <defs>
        <linearGradient id="meta-logo-grad-1" x1="61" y1="117" x2="259" y2="127" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0064e1" />
          <stop offset="0.4" stopColor="#0064e1" />
          <stop offset="0.83" stopColor="#0073ee" />
          <stop offset="1" stopColor="#0082fb" />
        </linearGradient>
        <linearGradient id="meta-logo-grad-2" x1="45" y1="139" x2="45" y2="66" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0082fb" />
          <stop offset="1" stopColor="#0064e0" />
        </linearGradient>
      </defs>
      <path
        d="m31.06 125.96c0 10.98 2.41 19.41 5.56 24.51 4.13 6.68 10.29 9.51 16.57 9.51 8.1 0 15.51-2.01 29.79-21.76 11.44-15.83 24.92-38.05 33.99-51.98l15.36-23.6c10.67-16.39 23.02-34.61 37.18-46.96C181.07 5.6 193.54 0 206.09 0c21.07 0 41.14 12.21 56.5 35.11 16.81 25.08 24.97 56.67 24.97 89.27 0 19.38-3.82 33.62-10.32 44.87-6.28 10.88-18.52 21.75-39.11 21.75v-31.02c17.63 0 22.03-16.2 22.03-34.74 0-26.42-6.16-55.74-19.73-76.69-9.63-14.86-22.11-23.94-35.84-23.94-14.85 0-26.8 11.2-40.23 31.17-7.14 10.61-14.47 23.54-22.7 38.13l-9.06 16.05c-18.2 32.27-22.81 39.62-31.91 51.75-15.95 21.24-29.57 29.29-47.5 29.29-21.27 0-34.72-9.21-43.05-23.09C3.34 156.6 0 141.76 0 124.85Z"
        fill="url(#meta-logo-grad-2)"
      />
      <path
        d="m24.49 37.3C38.73 15.35 59.28 0 82.85 0c13.65 0 27.22 4.04 41.39 15.61 15.5 12.65 32.02 33.48 52.63 67.81l7.39 12.32c17.84 29.72 27.99 45.01 33.93 52.22 7.64 9.26 12.99 12.02 19.94 12.02 17.63 0 22.03-16.2 22.03-34.74l27.4-.86c0 19.38-3.82 33.62-10.32 44.87-6.28 10.88-18.52 21.75-39.11 21.75-12.8 0-24.14-2.78-36.68-14.61-9.64-9.08-20.91-25.21-29.58-39.71l-25.79-43.08c-12.94-21.62-24.81-37.74-31.68-45.04-7.39-7.85-16.89-17.33-32.05-17.33-12.27 0-22.69 8.61-31.41 21.78Z"
        fill="url(#meta-logo-grad-1)"
      />
      <path
        d="M82.35 31.23c-12.27 0-22.69 8.61-31.41 21.78-12.33 18.61-19.88 46.33-19.88 72.95 0 10.98 2.41 19.41 5.56 24.51l-26.48 17.44C3.34 156.6 0 141.76 0 124.85 0 94.1 8.44 62.05 24.49 37.3 38.73 15.35 59.28 0 82.85 0Z"
        fill="url(#meta-logo-grad-2)"
      />
    </svg>
  );
}

function ElevenLabsLogo({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 694 90" className={className} aria-hidden>
      <path d="M248.261 22.1901H230.466L251.968 88.5124H271.123L292.625 22.1901H274.83L261.365 72.1488L248.261 22.1901Z" fill="white" />
      <path d="M0 0H18.413V88.5124H0V0Z" fill="white" />
      <path d="M36.5788 0H54.9917V88.5124H36.5788V0Z" fill="white" />
      <path d="M73.1551 0H127.652V14.7521H91.568V35.8264H125.181V50.5785H91.568V73.7603H127.652V88.5124H73.1551V0Z" fill="white" />
      <path d="M138.896 0H156.32V88.5124H138.896V0Z" fill="white" />
      <path d="M166.824 55.2893C166.824 31.1157 178.811 20.7025 197.471 20.7025C216.131 20.7025 226.759 30.9917 226.759 55.5372V59.5041H184.001C184.619 73.8843 188.944 78.719 197.224 78.719C203.773 78.719 207.851 74.876 208.593 68.1818H226.017C224.905 82.8099 212.795 90 197.224 90C177.452 90 166.824 79.4628 166.824 55.2893ZM209.582 47.9752C208.717 35.8264 204.515 31.8595 197.224 31.8595C189.933 31.8595 185.36 35.9504 184.125 47.9752H209.582Z" fill="white" />
      <path d="M295.962 55.2893C295.962 31.1157 307.949 20.7025 326.609 20.7025C345.269 20.7025 355.897 30.9917 355.897 55.5372V59.5041H313.139C313.757 73.8843 318.082 78.719 326.362 78.719C332.911 78.719 336.989 74.876 337.731 68.1818H355.155C354.043 82.8099 341.932 90 326.362 90C306.589 90 295.962 79.4628 295.962 55.2893ZM338.719 47.9752C337.854 35.8264 333.653 31.8595 326.362 31.8595C319.071 31.8595 314.498 35.9504 313.263 47.9752H338.719Z" fill="white" />
      <path d="M438.443 0H456.856V73.7603H491.457V88.5124H438.443V0Z" fill="white" />
      <path fillRule="evenodd" clipRule="evenodd" d="M495.783 55.2893C495.783 30 507.399 20.7025 522.352 20.7025C529.766 20.7025 536.563 24.9174 539.282 29.3802V22.1901H557.077V88.5124H539.776V80.7025C537.181 85.9091 529.89 90 521.857 90C506.04 90 495.783 79.8347 495.783 55.2893ZM526.924 33.719C535.574 33.719 540.27 40.2893 540.27 55.2893C540.27 70.2893 535.574 76.9835 526.924 76.9835C518.274 76.9835 513.331 70.2893 513.331 55.2893C513.331 40.2893 518.274 33.719 526.924 33.719Z" fill="white" />
      <path fillRule="evenodd" clipRule="evenodd" d="M587.847 80.7025V88.5124H570.547V0H587.971V29.3802C590.937 24.7934 597.857 20.7025 605.272 20.7025C619.854 20.7025 631.47 30 631.47 55.2893C631.47 80.5785 620.101 90 604.901 90C596.869 90 590.319 85.9091 587.847 80.7025ZM600.329 33.843C608.979 33.843 613.922 40.2893 613.922 55.2893C613.922 70.2893 608.979 76.9835 600.329 76.9835C591.678 76.9835 586.982 70.2893 586.982 55.2893C586.982 40.2893 591.678 33.843 600.329 33.843Z" fill="white" />
      <path d="M638.638 68.8017H656.062C656.309 75.7438 660.016 79.0909 666.566 79.0909C673.115 79.0909 676.823 76.1157 676.823 70.9091C676.823 66.1983 673.981 64.4628 667.802 62.9752L662.488 61.6116C647.412 57.7686 639.873 53.6777 639.873 41.157C639.873 28.6364 651.49 20.7025 666.319 20.7025C681.148 20.7025 692.394 26.5289 692.888 40.2893H675.463C675.093 34.2149 671.385 31.6116 666.072 31.6116C660.758 31.6116 657.05 34.2149 657.05 39.1736C657.05 43.7603 660.016 45.4959 665.207 46.7355L670.644 48.0992C684.979 51.6942 694 55.2893 694 68.6777C694 82.0661 682.137 90 666.072 90C648.647 90 639.008 83.4297 638.638 68.8017Z" fill="white" />
      <path d="M384.072 49.4628C384.072 39.0496 389.015 33.3471 396.677 33.3471C402.979 33.3471 406.563 37.314 406.563 45.8678V88.5124H423.987V43.1405C423.987 27.7686 415.337 20.7025 402.732 20.7025C394.205 20.7025 387.162 25.0413 384.072 30.7438V22.1901H366.401V88.5124H384.072V49.4628Z" fill="white" />
    </svg>
  );
}

function buildJsonHeaders() {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
  return {
    "Content-Type": "application/json",
    ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
  };
}

function uniqVoiceEntries(voices = []) {
  const seen = new Set();

  return (voices || []).filter((voice) => {
    const voiceId = String(voice?.voice_id || "").trim();
    if (!voiceId || seen.has(voiceId)) return false;
    seen.add(voiceId);
    return true;
  });
}

function normalizeVoiceCategoryKey(category) {
  const normalized = String(category || "").trim().toLowerCase();
  return normalized || "uncategorized";
}

function formatVoiceCategoryTitle(category) {
  const normalized = normalizeVoiceCategoryKey(category);
  if (VOICE_ACCESS_CATEGORY_LABELS[normalized]) return VOICE_ACCESS_CATEGORY_LABELS[normalized];
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatVoiceAccessDescription(category) {
  const title = formatVoiceCategoryTitle(category).toLowerCase();
  return `Choose which ${title} voices are available to regular users.`;
}

function compareVoiceCategoryKeys(left, right) {
  const leftIndex = VOICE_ACCESS_CATEGORY_ORDER.indexOf(left);
  const rightIndex = VOICE_ACCESS_CATEGORY_ORDER.indexOf(right);

  if (leftIndex !== -1 || rightIndex !== -1) {
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  }

  return left.localeCompare(right);
}

function buildVoiceAccessCatalogs(voices = []) {
  return uniqVoiceEntries(voices).reduce((result, voice) => {
    const category = normalizeVoiceCategoryKey(voice?.category);
    if (!result[category]) result[category] = [];
    result[category].push({
      ...voice,
      category
    });
    return result;
  }, {});
}

function buildVoiceAccessCategories(voiceAccessCatalogs = {}, voiceAccess = {}) {
  const keys = uniqStrings([
    ...Object.keys(voiceAccessCatalogs || {}).map((category) => normalizeVoiceCategoryKey(category)),
    ...Object.keys(voiceAccess || {}).map((category) => normalizeVoiceCategoryKey(category))
  ]).sort(compareVoiceCategoryKeys);

  return keys.map((category) => ({
    key: category,
    title: formatVoiceCategoryTitle(category),
    description: formatVoiceAccessDescription(category)
  }));
}

function buildEmptyVoiceAccessCatalogs() {
  return {};
}

function linesToArray(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function priceInputToCents(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return Math.round(amount * 100);
}

function formatPlanPriceInput(priceCents) {
  const value = Number(priceCents);
  if (!Number.isFinite(value)) return "0.00";
  return (value / 100).toFixed(2);
}

function buildElevenlabsSyncMessage({ voices, voiceAccessCatalogs }) {
  const scopeMessage = voices.length ? `ElevenLabs voices synced: ${voices.length}.` : "No ElevenLabs voices found for the selected scope.";
  const categorySummary = buildVoiceAccessCategories(voiceAccessCatalogs)
    .map((category) => `${category.title}: ${(voiceAccessCatalogs?.[category.key] || []).length}`)
    .join(", ");

  return categorySummary ? `${scopeMessage} ${categorySummary}.` : scopeMessage;
}

function firstError(errors) {
  if (!errors || typeof errors !== "object") return "";
  const firstKey = Object.keys(errors)[0];
  const value = errors[firstKey];
  if (Array.isArray(value)) return value[0];
  return typeof value === "string" ? value : "";
}

function resolveDefaultVoiceId(currentVoiceId, voices) {
  const availableVoiceIds = voices.map((voice) => voice.voice_id);
  if (currentVoiceId && availableVoiceIds.includes(currentVoiceId)) {
    return currentVoiceId;
  }

  return voices[0]?.voice_id || currentVoiceId || "";
}

function formatVoiceOptionLabel(voice) {
  const parts = [voice?.name || voice?.voice_id, voice?.category, voice?.voice_type].filter(Boolean);
  return parts.join(" · ");
}

function hasExplicitVoiceAccessCategory(category, voiceAccess) {
  return Object.prototype.hasOwnProperty.call(voiceAccess || {}, category);
}

function effectiveVoiceAccessIds(category, elevenlabsSettings, voiceAccessCatalogs) {
  const configuredIds = elevenlabsSettings?.voice_access?.[category];
  if (Array.isArray(configuredIds)) {
    return uniqStrings(configuredIds);
  }

  return uniqStrings((voiceAccessCatalogs?.[category] || []).map((voice) => voice.voice_id));
}

function isProviderVerified(setting, provider) {
  return Boolean(setting?.provider_health?.[provider]?.valid);
}

function providerStateLabel(health, configured) {
  if (!configured) return "Not configured";
  if (health?.valid) return "Verified";
  if (health?.checked_at) return "Check failed";
  return "Verification pending";
}

function providerStateColor(health) {
  if (health?.valid) return "text-emerald-300";
  if (health?.configured) return health?.checked_at ? "text-rose-300" : "text-amber-300";
  return "text-amber-300";
}

function providerStateBadge(health) {
  if (health?.valid) return "border border-emerald-500/50 bg-emerald-500/10 text-emerald-200";
  if (health?.configured) return health?.checked_at ? "border border-rose-500/50 bg-rose-500/10 text-rose-200" : "border border-amber-500/50 bg-amber-500/10 text-amber-200";
  return "border border-slate-700 bg-slate-900 text-slate-300";
}

function providerSuggestionOptions(provider, providerCatalog, providerModels) {
  const suggestions = providerCatalog?.[provider]?.models || [];
  const current = providerModels?.[provider] || [];
  return uniqStrings([...suggestions, ...current]);
}

function providerPlaceholder(provider) {
  switch (provider) {
  case "openrouter":
    return "e.g. openai/gpt-5.4";
  case "fal":
    return "e.g. fal-ai/flux/schnell";
  case "wavespeed":
    return "e.g. google/nano-banana-2/edit";
  case "elevenlabs":
    return "e.g. eleven_v3";
  default:
    return "Add provider model slug";
  }
}

function includeCurrentSelection(options, currentValue) {
  const normalized = uniqStrings(options || []);
  if (currentValue && !normalized.includes(currentValue)) {
    return [currentValue, ...normalized];
  }
  return normalized;
}

function imageModelsForProvider(provider, providerModels) {
  if (provider === "wavespeed") {
    return getWavespeedImageModels(providerModels.wavespeed || []);
  }
  return getFalImageModels(providerModels.fal || []);
}

function imageEditModelsForProvider(provider, providerModels) {
  if (provider === "wavespeed") {
    return getWavespeedImageEditModels(providerModels.wavespeed || []);
  }
  return getFalImageEditModels(providerModels.fal || []);
}

function workflowResolutionOptions(provider, model) {
  if (provider === "wavespeed") {
    return WAVESPEED_IMAGE_RESOLUTION_OPTIONS_BY_MODEL[model] || [];
  }
  return FAL_IMAGE_RESOLUTION_OPTIONS_BY_MODEL[model] || [];
}

function resolveWorkflowResolution(currentValue, options) {
  const normalizedOptions = uniqStrings(options || []);
  if (normalizedOptions.length === 0) return "";

  const current = String(currentValue || "").trim();
  const matchedCurrent = normalizedOptions.find((option) => option.toLowerCase() === current.toLowerCase());
  if (matchedCurrent) return matchedCurrent;

  return normalizedOptions.find((option) => option.toLowerCase() === "2k") || normalizedOptions[0];
}

function getFalImageModels(models) {
  const all = uniqStrings(models);
  const edit = new Set(getFalImageEditModels(all));
  const video = new Set(getFalVideoModels(all));
  return all.filter((model) => !edit.has(model) && !video.has(model));
}

function getFalImageEditModels(models) {
  const all = uniqStrings(models);
  return all.filter((model) => model.endsWith("/edit") || model.includes("image-to-image"));
}

function getFalVideoModels(models) {
  const all = uniqStrings(models);
  return all.filter((model) => model.endsWith("/video") || model.endsWith("/image-to-video") || model.endsWith("/text-to-video") || model.includes("creatify"));
}

function getWavespeedImageModels(models) {
  const all = uniqStrings(models);
  const edit = new Set(getWavespeedImageEditModels(all));
  return all.filter((model) => !edit.has(model));
}

function getWavespeedImageEditModels(models) {
  const all = uniqStrings(models);
  return all.filter((model) => model.endsWith("/edit") || model.endsWith("/image-edit") || model.includes("image-edit"));
}

function uniqStrings(values) {
  return [...new Set((values || []).map((value) => String(value || "").trim()).filter(Boolean))];
}

function formatIsoDateTime(value) {
  if (!value) return "Not available";

  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "Not available";
  }
}

function ProviderIdentity({ provider, title, subtitle }) {
  return (
    <div className="flex items-center gap-3">
      <ProviderLogo provider={provider} />
      <div>
        <p className="text-sm font-semibold text-slate-100">{title}</p>
        {subtitle ? <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-500">{subtitle}</p> : null}
      </div>
    </div>
  );
}

function ProviderLogo({ provider, className }) {
  const tone = providerMeta[provider]?.logoTone || "border-slate-700 bg-slate-900 text-slate-100";

  return (
    <span
      className={cn(
        "flex size-11 items-center justify-center rounded-2xl border backdrop-blur-sm",
        tone,
        className
      )}
      aria-hidden="true"
    >
      <ProviderLogoGlyph provider={provider} />
    </span>
  );
}

function ProviderLogoGlyph({ provider }) {
  switch (provider) {
  case "fal":
    return (
      <svg viewBox="0 0 24 24" className="size-6" aria-hidden="true">
        <path
          clipRule="evenodd"
          d="M15.477 0c.415 0 .749.338.788.752a7.775 7.775 0 006.985 6.984c.413.04.752.373.752.788v6.952c0 .415-.338.748-.752.788a7.775 7.775 0 00-6.985 6.984c-.04.414-.373.752-.788.752H8.525c-.416 0-.749-.338-.789-.752a7.775 7.775 0 00-6.984-6.984c-.414-.04-.752-.373-.752-.788V8.524c0-.415.338-.748.752-.788A7.775 7.775 0 007.736.752C7.776.338 8.11 0 8.526 0h6.95zM4.819 11.98a7.226 7.226 0 007.223 7.23 7.226 7.226 0 007.223-7.23c0-3.994-3.234-7.23-7.223-7.23a7.227 7.227 0 00-7.223 7.23z"
          fill="#EC0648"
          fillRule="evenodd"
        />
      </svg>
    );
  case "wavespeed":
    return (
      <svg viewBox="0 0 200 200" className="size-6" aria-hidden="true">
        <rect width="200" height="200" rx="42" fill="#000" />
        <path
          d="M50 142c0-17.9 7.2-33.7 20-48.6 11.5-13.2 27.2-24.8 44.7-35.4 12.9-7.8 28.1-16.5 37.9-25.8 5.9-5.6 10.3-12.1 12.9-19.5h-29.7c-3.1 5.7-8.4 11.8-17.2 17.8-11.4 7.8-25.9 15.3-40 23.7-20.2 12.1-39.2 26.4-53.8 44-17.3 20.8-25.8 44.3-25.8 67.1 0 6.7.7 13.2 2.1 19.5h34.5c-1.5-6.1-2.4-12.6-2.4-19.5zm50.8 19.5H128c2.9-5.3 8.1-10.9 16.1-16.6 11.8-8.5 26.8-16.9 41.8-25.9 4.1-2.5 8.2-5 12.1-7.6v-34.8c-8.9 7-19 13.2-28.9 19.1-13.4 8-27.4 15.5-39.3 24-15.8 11.2-28.8 24.2-35 41.8zm29.5-158h-31.9c-.7 1.1-1.5 2.1-2.3 3.2-11.7 16-27.6 30-44.9 42.6C38.2 74.7 25.8 84 15.5 92.8v35.5c14.1-13.2 30.2-25.6 46.6-37.6 13.7-10 27.8-19.7 39.9-31.4 10-9.7 17.8-20.5 27-35.8z"
          fill="#fff"
        />
      </svg>
    );
  case "openrouter":
    return (
      <svg viewBox="0 0 32 32" className="size-6" aria-hidden="true">
        <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="1.8" opacity="0.95" />
        <circle cx="16" cy="16" r="3.2" fill="currentColor" />
        <path d="M8.2 12.9c1.9-3.2 4.6-4.9 7.8-4.9 3.5 0 6.1 1.6 7.8 4.9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8.2 19.1c1.9 3.2 4.6 4.9 7.8 4.9 3.5 0 6.1-1.6 7.8-4.9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  case "elevenlabs":
    return (
      <svg viewBox="0 0 220 90" className="size-6" aria-hidden="true">
        <path d="M41 2h29.6L29.8 88H0L41 2zm69.1 0h29.6L98.9 88H69.3L110.1 2z" fill="currentColor" />
      </svg>
    );
  default:
    return null;
  }
}

function HeaderActionIcon({ children, className }) {
  return (
    <span
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-400/10 text-cyan-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]",
        className
      )}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

function IconOpenApp({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("h-4 w-4 fill-none stroke-current stroke-[1.9]", className)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 5h5v5" />
      <path d="M10 14L19 5" />
      <path d="M19 13v4a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4" />
    </svg>
  );
}

function IconCatalog({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("h-4 w-4 fill-none stroke-current stroke-[1.9]", className)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a7.5 7.5 0 0 1 7.3 5.7" />
      <path d="M21 3v5h-5" />
      <path d="M12 21a7.5 7.5 0 0 1-7.3-5.7" />
      <path d="M3 21v-5h5" />
      <path d="M8.5 8.5h3v3h-3z" />
      <path d="M12.5 12.5h3v3h-3z" />
    </svg>
  );
}

function IconVerify({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("h-4 w-4 fill-none stroke-current stroke-[1.9]", className)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v5c0 4.2-2.6 7.9-7 10-4.4-2.1-7-5.8-7-10V6l7-3z" />
      <path d="M9.5 12.2l1.8 1.8 3.6-4" />
    </svg>
  );
}

function IconSave({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("h-4 w-4 fill-none stroke-current stroke-[1.9]", className)} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 5h11l3 3v11H5z" />
      <path d="M8 5v5h7V5" />
      <path d="M8 17h8" />
    </svg>
  );
}
